import { utc, UTCDateMini } from "@date-fns/utc"
import {
  constructFrom,
  eachDayOfInterval,
  getDaysInMonth,
  isAfter,
  startOfMonth,
  startOfWeek,
} from "date-fns"
import { sortBy, sum } from "lodash"
import { type SalesAndForecast } from "~/models/variant"

import {
  convertToEndOfWeekUTC,
  convertToStartOfMonthUTC,
  convertToStartOfWeekUTC,
  DateFormatType,
  formatDate,
  getStartOfCurrentMonthUTC,
  getStartOfCurrentWeekUTC,
  getStartOfWeekDatesBetween,
  startOfDayUTC,
} from "~/utils/datetime"
import {
  dateToFiscal,
  type FiscalConfig,
  fiscalPeriodMonthStart,
  fiscalPeriodWeekMondays,
} from "~/utils/fiscalCalendar"
import { getStoredFiscalConfig } from "~/stores/fiscal-config-store"

type DateData = {
  date: Date
  quantity: number | null
  revenue?: number
  discountQuantity?: number | null
}

function distributeWeeklyData(weeklyData: DateData[]) {
  return weeklyData.flatMap(({ date, quantity, revenue, discountQuantity }) => {
    const start = convertToStartOfWeekUTC(date)
    const end = convertToEndOfWeekUTC(date)
    const days = eachDayOfInterval({
      start: new UTCDateMini(start),
      end: new UTCDateMini(end),
    }).map(startOfDayUTC)
    const dailyQuantity = (quantity ?? 0) / days.length
    const dailyRevenue = (revenue ?? 0) / days.length
    const dailyDiscountQuantity = (discountQuantity ?? 0) / days.length

    return days.map((day) => ({
      date: day,
      quantity: quantity !== null ? dailyQuantity : null,
      revenue: dailyRevenue,
      discountQuantity: discountQuantity !== null ? dailyDiscountQuantity : null,
    }))
  })
}

function aggregateIntoMonthlyData(dailyData: DateData[]) {
  const monthlyAggregates: Map<
    string,
    {
      quantity: number | null
      dayCount: number
      revenue?: number
      discountQuantity?: number | null
    }
  > = new Map()
  let largestDateProcessed = new Date(1970, 0, 0)

  dailyData
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .forEach(({ date, quantity, revenue, discountQuantity }) => {
      const startOfMonthKey = formatDate(date, DateFormatType.YearMonthKey)
      const currentMonth = monthlyAggregates.get(startOfMonthKey)

      monthlyAggregates.set(startOfMonthKey, {
        revenue: (currentMonth?.revenue ?? 0) + (revenue ?? 0),
        quantity:
          quantity === null &&
          (currentMonth?.quantity === undefined || currentMonth.quantity === null)
            ? null
            : (currentMonth?.quantity ?? 0) + (quantity ?? 0),
        dayCount:
          date.getTime() > largestDateProcessed.getTime()
            ? (currentMonth?.dayCount ?? 0) + 1
            : (currentMonth?.dayCount ?? 0),
        discountQuantity:
          discountQuantity === null &&
          (currentMonth?.discountQuantity === undefined || currentMonth.discountQuantity === null)
            ? null
            : (currentMonth?.discountQuantity ?? 0) + (discountQuantity ?? 0),
      })
      largestDateProcessed = date
    })

  return Array.from(monthlyAggregates, ([startOfMonth, values]) => ({
    startOfMonth: startOfDayUTC(startOfMonth),
    values,
  }))
}

// Fiscal counterpart of the day-distribution below: weeks map whole into fiscal periods
// (both are Mon–Sun), so a period is the plain sum of its weeks, keyed by the 1st of the
// calendar month the period represents. Mirrors the calendar path's completeness rule:
// only periods with every week present are returned, plus the current period when
// `includeCurrentMonth` is set.
function aggregateIntoFiscalPeriods(
  weeklyData: DateData[],
  config: FiscalConfig,
  includeCurrentPeriod: boolean,
) {
  type Agg = {
    fiscalYear: number
    period: number
    quantity: number | null
    revenue: number
    discountQuantity: number | null
    weeks: Set<number>
  }
  const byPeriod = new Map<string, Agg>()
  for (const { date, quantity, revenue, discountQuantity } of weeklyData) {
    const { fiscalYear, fiscalPeriod, weekIndex } = dateToFiscal(
      convertToStartOfWeekUTC(date),
      config,
    )
    const key = `${fiscalYear}-${fiscalPeriod}`
    const agg = byPeriod.get(key) ?? {
      fiscalYear,
      period: fiscalPeriod,
      quantity: null,
      revenue: 0,
      discountQuantity: null,
      weeks: new Set<number>(),
    }
    // Round per week so the period equals the sum of the rounded weekly cells users see
    if (quantity !== null) agg.quantity = (agg.quantity ?? 0) + Math.round(quantity)
    agg.revenue += revenue ?? 0
    if (discountQuantity != null)
      agg.discountQuantity = (agg.discountQuantity ?? 0) + discountQuantity
    agg.weeks.add(weekIndex)
    byPeriod.set(key, agg)
  }

  const current = dateToFiscal(getStartOfCurrentWeekUTC(), config)
  return [...byPeriod.values()]
    .filter((agg) => {
      const isCurrent = agg.fiscalYear === current.fiscalYear && agg.period === current.fiscalPeriod
      const complete =
        agg.weeks.size >= fiscalPeriodWeekMondays(agg.fiscalYear, agg.period, config).length
      return complete || (isCurrent && includeCurrentPeriod)
    })
    .map((agg) => ({
      date: fiscalPeriodMonthStart(agg.fiscalYear, agg.period, config),
      quantity: agg.quantity,
      revenue: agg.revenue,
      discountQuantity: agg.discountQuantity,
    }))
    .sort((a, b) => a.date.getTime() - b.date.getTime())
}

/*
 * Converts weekly data to monthly data by distributing the weekly data over the days of the week and then aggregating it into months.
 * OBS: It only returns full months (meaning if some months are not entirely covered by weekly data, it won't be included).
 *
 * This method will aggregate datapoints with duplicate dates
 * @param weeklyData - The weekly data to convert.
 * With a `fiscalConfig`, "months" are the company's fiscal periods instead: whole weeks summed
 * per period, keyed by the period's calendar month (no day-distribution needed — week and
 * period boundaries are both Mondays).
 */
export function convertWeeklyToFullMonthly(
  weeklyData: DateData[],
  startDate?: Date,
  endDate?: Date,
  includeCurrentMonth = false,
  fiscalConfig: FiscalConfig | null = getStoredFiscalConfig(),
) {
  const withMissingWeeks = fillInMissingWeeks(weeklyData, startDate, endDate)
  if (fiscalConfig) {
    return aggregateIntoFiscalPeriods(withMissingWeeks, fiscalConfig, includeCurrentMonth)
  }
  const dailyData = distributeWeeklyData(withMissingWeeks)
  const monthlyData = aggregateIntoMonthlyData(dailyData)

  const thisMonth = startOfMonth(getStartOfCurrentWeekUTC())

  return monthlyData
    .filter(
      (month) =>
        (month.startOfMonth.getTime() === thisMonth.getTime() && includeCurrentMonth) ||
        month.values.dayCount === getDaysInMonth(month.startOfMonth, { in: utc }),
    )
    .map((month) => ({
      date: month.startOfMonth,
      quantity: month.values.quantity,
      revenue: month.values.revenue,
      discountQuantity: month.values.discountQuantity,
    }))
    .sort((a, b) => a.date.getTime() - b.date.getTime())
}

export function getStartOfInInterval(
  startDate: Date,
  endDate: Date,
  interval: "weekly" | "monthly" | "quarterly",
) {
  const rawDates = eachDayOfInterval({ start: startDate, end: endDate }, { in: utc })
  if (interval === "weekly") {
    return rawDates.filter((date) => date.getDay() === 1).map(startOfDayUTC)
  }
  if (interval === "monthly") {
    return rawDates.filter((date) => date.getDate() === 1).map(startOfDayUTC)
  }
  return rawDates
    .filter((date) => date.getMonth() % 3 === 0 && date.getDate() === 1)
    .map(startOfDayUTC)
}

function fillInMissingWeeks(weeklyData: DateData[], startDate?: Date, endDate?: Date) {
  if (weeklyData.length === 0) return []

  const sortedWeeklyData = sortBy(weeklyData, "date")
  const start = startDate ?? sortedWeeklyData[0]!.date
  const end = endDate ?? sortedWeeklyData[sortedWeeklyData.length - 1]!.date

  const weeks = eachDayOfInterval({ start, end }, { in: utc })
    .filter((date) => date.getDay() === 1)
    .map(startOfDayUTC)
  const missingWeeks = weeks.filter(
    (week) => !weeklyData.some((data) => data.date.getTime() === week.getTime()),
  )

  return sortBy([...weeklyData, ...missingWeeks.map((date) => ({ date, quantity: null }))], "date")
}

// TODO: Does this belong here?
export const isFutureForecast = (date: Date, monthly: boolean) => {
  const convertedDate = monthly ? convertToStartOfMonthUTC(date) : convertToStartOfWeekUTC(date)
  const convertedMaxDate = monthly ? getStartOfCurrentMonthUTC() : getStartOfCurrentWeekUTC()
  return convertedDate >= convertedMaxDate
}

// TODO: Add leap year support, probably through the use of input parameter
const MONTHS = [
  { name: "January", days: 31 },
  { name: "February", days: 28 },
  { name: "March", days: 31 },
  { name: "April", days: 30 },
  { name: "May", days: 31 },
  { name: "June", days: 30 },
  { name: "July", days: 31 },
  { name: "August", days: 31 },
  { name: "September", days: 30 },
  { name: "October", days: 31 },
  { name: "November", days: 30 },
  { name: "December", days: 31 },
]
export const distributeToGenericMonthly = (data: { week: number; value: number }[]) => {
  const out: { name: string; value: number }[] = []
  let daysPast = 0
  const dayData: number[] = Array(366).fill(0)
  data.forEach((p) => {
    for (let i = 0; i < 7; i++) {
      dayData[p.week * 7 + i] = p.value / 7 + (dayData[p.week * 7 + i] || 0)
    }
  })

  for (const month of MONTHS) {
    const monthName = month.name
    const monthDays = month.days
    const startDate = daysPast
    const endDate = daysPast + monthDays
    const combined = sum(dayData.slice(startDate, endDate))
    daysPast += monthDays
    out.push({ name: monthName, value: combined })
  }
  return out
}

export const addMissingDatesSalesAndForecast = (
  data: SalesAndForecast[],
  startDate: Date,
  endDate: Date,
) => {
  // Matches isSameWeek(x.date, date), which builds both dates with x.date's constructor
  const sample = data[0]?.date
  const weekOf = (date: Date) => startOfWeek(sample ? constructFrom(sample, date) : date).getTime()
  const dataWeeks = new Set(data.map((x) => weekOf(x.date)))
  const missingDates = getStartOfWeekDatesBetween(startDate, endDate).filter(
    (date) => !dataWeeks.has(weekOf(date)),
  )

  return sortBy(
    data.concat(
      missingDates.map((date) => ({
        date,
        quantity: null,
        future: isAfter(date, getStartOfCurrentWeekUTC()),
      })),
    ),
    "date",
  )
}
